import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, RefreshCw, AlertCircle, AlertTriangle, Trophy, ChevronDown, ChevronUp } from 'lucide-react';
import { useEvaluation } from '@/hooks/queries';
import { useEvaluationResults } from '@/hooks/queries/useEvaluationResults';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Progress } from '@/components/ui/progress';
import { ParticipantType, Importance } from '@/types/evaluation.types';
import type { EvaluationSummary, AttentionItem } from '@/services/scoring';

export function EvaluationResultsPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation(['results', 'common']);

  // Fetch evaluation data
  const { data: evaluation, isLoading: isLoadingEvaluation } = useEvaluation(id || '');

  // Calculate MCDA results
  const {
    data: summary,
    isLoading: isLoadingResults,
    error,
    refetch,
  } = useEvaluationResults(id);

  // Redirect if no ID
  if (!id) {
    navigate('/');
    return null;
  }

  // Show loading state
  if (isLoadingEvaluation || isLoadingResults) {
    return (
      <div className="container mx-auto py-8">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">{t('common:loading')}</p>
          </div>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="container mx-auto py-8">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>
            {error instanceof Error ? error.message : 'Failed to calculate results'}
          </AlertDescription>
        </Alert>
        <Button
          className="mt-4"
          variant="outline"
          onClick={() => navigate(`/evaluation/${id}`)}
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          {t('common:actions.back')}
        </Button>
      </div>
    );
  }

  // Redirect if evaluation not found
  if (!evaluation || !summary) {
    navigate('/');
    return null;
  }

  return (
    <div className="container mx-auto py-8 space-y-6">
      {/* Back button and refresh */}
      <div className="flex items-center justify-between">
        <Button
          variant="ghost"
          onClick={() => navigate(`/evaluation/${id}`)}
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          {t('common:actions.back')}
        </Button>

        <Button variant="outline" size="sm" onClick={() => refetch()}>
          <RefreshCw className="h-4 w-4 mr-2" />
          {t('results:refresh')}
        </Button>
      </div>

      {/* Page header */}
      <div>
        <h1 className="text-3xl font-bold">{evaluation.name}</h1>
        <p className="text-muted-foreground">{t('results:title')}</p>
        <p className="text-sm text-muted-foreground mt-2">
          {t('results:lastUpdated')}: {summary.lastCalculated.toLocaleString()}
        </p>
      </div>

      {/* Solution rankings */}
      <SolutionRankingsCard summary={summary} />

      {/* Participant coverage */}
      <ParticipantCoverageCard summary={summary} />

      {/* Attention items */}
      {summary.attentionItems.length > 0 && (
        <div id="attention-items-section">
          <AttentionItemsCard items={summary.attentionItems} />
        </div>
      )}

      {/* Scoring ledger */}
      <ScoringLedgerCard summary={summary} />
    </div>
  );
}

/**
 * Solution Rankings Card Component
 */
function SolutionRankingsCard({ summary }: { summary: EvaluationSummary }) {
  const { t } = useTranslation('results');
  const [expandedSolutions, setExpandedSolutions] = useState<Set<string>>(new Set());

  const toggleExpanded = (solutionId: string) => {
    setExpandedSolutions(prev => {
      const next = new Set(prev);
      if (next.has(solutionId)) {
        next.delete(solutionId);
      } else {
        next.add(solutionId);
      }
      return next;
    });
  };

  const scrollToAttentionItems = () => {
    const element = document.getElementById('attention-items-section');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('solutionRankings.title')}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {summary.solutionScores.map((solution) => {
          const isExpanded = expandedSolutions.has(solution.solutionId);
          const solutionAttentionItems = summary.attentionItems.filter(item =>
            item.affectedItems.some(ai => ai.solutionId === solution.solutionId)
          );

          // Group attention items by type and severity
          const criticalLowScores = solutionAttentionItems.filter(
            item => item.type === 'critical-low-score'
          );
          const unscoredItems = solutionAttentionItems.filter(
            item => item.type === 'unscored-criterion'
          );

          return (
            <div key={solution.solutionId} className="border rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="text-2xl font-bold text-muted-foreground">
                    #{solution.rank}
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="text-lg font-semibold">{solution.solutionName}</h3>
                      {solution.isWinner && (
                        <Badge variant="default" className="bg-green-600">
                          <Trophy className="h-3 w-3 mr-1" />
                          {t('solutionRankings.winner')}
                        </Badge>
                      )}
                    </div>
                    {solutionAttentionItems.length > 0 && (
                      <div className="flex gap-1 mt-1">
                        {criticalLowScores.length > 0 && (
                          <Badge
                            variant="destructive"
                            className="cursor-pointer hover:bg-destructive/80 transition-colors"
                            onClick={scrollToAttentionItems}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                scrollToAttentionItems();
                              }
                            }}
                            aria-label={`${criticalLowScores.length} critical low scores. Click to view details.`}
                          >
                            <AlertCircle className="h-3 w-3 mr-1" />
                            {criticalLowScores.length} {t('attentionItems.criticalLowScore')}
                          </Badge>
                        )}
                        {unscoredItems.length > 0 && (
                          <Badge
                            variant="secondary"
                            className="cursor-pointer hover:bg-secondary/80 transition-colors"
                            onClick={scrollToAttentionItems}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                scrollToAttentionItems();
                              }
                            }}
                            aria-label={`${unscoredItems.length} unscored criteria. Click to view details.`}
                          >
                            <AlertTriangle className="h-3 w-3 mr-1" />
                            {unscoredItems.length} {t('attentionItems.unscoredInProgress')}
                          </Badge>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <div className="text-right">
                    <div className="text-3xl font-bold">
                      {(solution.totalScore * 100).toFixed(1)}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {t('solutionRankings.outOf')}
                    </div>
                  </div>

                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => toggleExpanded(solution.solutionId)}
                  >
                    {isExpanded ? (
                      <ChevronUp className="h-4 w-4" />
                    ) : (
                      <ChevronDown className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </div>

              {isExpanded && (
                <div className="mt-4 space-y-2">
                  <p className="text-sm font-semibold">{t('solutionRankings.groupBreakdown')}:</p>
                  {solution.groupScores.map(group => {
                    // Find attention items for criteria in this group
                    const groupAttentionItems = summary.attentionItems.filter(item =>
                      item.affectedItems.some(ai =>
                        ai.solutionId === solution.solutionId &&
                        group.criterionScores.some(cs => cs.criterionId === ai.criterionId)
                      )
                    );

                    const groupCriticalCount = groupAttentionItems.filter(
                      item => item.type === 'critical-low-score'
                    ).length;
                    const groupUnscoredCount = groupAttentionItems.filter(
                      item => item.type === 'unscored-criterion'
                    ).length;

                    return (
                      <div key={group.groupId} className="flex justify-between text-sm items-center">
                        <span className="text-muted-foreground flex items-center gap-2">
                          {group.groupName} ({(group.groupWeight * 100).toFixed(0)}% {t('scoringLedger.groupWeight', { weight: '' }).trim()})
                          {groupCriticalCount > 0 && (
                            <span
                              className="inline-flex items-center text-destructive cursor-pointer hover:opacity-80"
                              onClick={scrollToAttentionItems}
                              role="button"
                              tabIndex={0}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  scrollToAttentionItems();
                                }
                              }}
                              aria-label={`${groupCriticalCount} critical issues in ${group.groupName}. Click to view details.`}
                            >
                              <AlertCircle className="h-3 w-3" />
                              <span className="text-xs ml-0.5">{groupCriticalCount}</span>
                            </span>
                          )}
                          {groupUnscoredCount > 0 && (
                            <span
                              className="inline-flex items-center text-yellow-600 cursor-pointer hover:opacity-80"
                              onClick={scrollToAttentionItems}
                              role="button"
                              tabIndex={0}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  scrollToAttentionItems();
                                }
                              }}
                              aria-label={`${groupUnscoredCount} unscored criteria in ${group.groupName}. Click to view details.`}
                            >
                              <AlertTriangle className="h-3 w-3" />
                              <span className="text-xs ml-0.5">{groupUnscoredCount}</span>
                            </span>
                          )}
                        </span>
                        <span className="font-medium">
                          {group.rawScore.toFixed(3)} {t('scoringLedger.rawAverage', { avg: '' }).trim()} × {(group.groupWeight * 100).toFixed(0)}% = {(group.weightedScore * 100).toFixed(1)}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}

/**
 * Participant Coverage Card Component
 */
function ParticipantCoverageCard({ summary }: { summary: EvaluationSummary }) {
  const { t } = useTranslation('results');

  // Separate hosts with and without scorecards
  const hostsWithScorecards = summary.participantCoverage.filter(
    p => p.participantType === ParticipantType.Host && p.hasScorecard
  );
  const hostsWithoutScorecards = summary.participantCoverage.filter(
    p => p.participantType === ParticipantType.Host && !p.hasScorecard
  );
  const evaluators = summary.participantCoverage.filter(
    p => p.participantType === ParticipantType.Evaluator
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('participantCoverage.title')}</CardTitle>
        <CardDescription>
          {t('participantCoverage.description', {
            count: summary.participantCoverage.length,
          })}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Hosts without scorecards - simple list */}
        {hostsWithoutScorecards.length > 0 && (
          <div>
            <h4 className="font-semibold mb-2">{t('participantCoverage.hosts')}</h4>
            <div className="flex flex-wrap gap-2">
              {hostsWithoutScorecards.map(p => (
                <Badge key={p.participantId} variant="outline">
                  {p.participantName}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {/* Participants table - evaluators and hosts with scorecards */}
        {(evaluators.length > 0 || hostsWithScorecards.length > 0) && (
          <div>
            <h4 className="font-semibold mb-2">{t('participantCoverage.participants')}</h4>
            <ParticipantTable participants={[...hostsWithScorecards, ...evaluators]} />
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function ParticipantTable({
  participants,
}: {
  participants: EvaluationSummary['participantCoverage'];
}) {
  const { t } = useTranslation('results');

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>{t('participantCoverage.name')}</TableHead>
          <TableHead>{t('participantCoverage.assignedGroups')}</TableHead>
          <TableHead>{t('participantCoverage.progress')}</TableHead>
          <TableHead>{t('participantCoverage.status')}</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {participants.map(p => (
          <TableRow key={p.participantId}>
            <TableCell className="font-medium">{p.participantName}</TableCell>
            <TableCell>
              {p.assignedGroupNames.length === 0 ? (
                <span className="text-muted-foreground">
                  {t('participantCoverage.allGroups')}
                </span>
              ) : (
                <div className="flex flex-wrap gap-1">
                  {p.assignedGroupNames.map(name => (
                    <Badge key={name} variant="outline">
                      {name}
                    </Badge>
                  ))}
                </div>
              )}
            </TableCell>
            <TableCell>
              <div className="flex items-center gap-2">
                <Progress value={p.completionPercentage} className="w-24" />
                <span className="text-sm text-muted-foreground">
                  {p.completionPercentage.toFixed(0)}%
                </span>
              </div>
              <div className="text-xs text-muted-foreground mt-1">
                {t('participantCoverage.criteriaCount', {
                  scored: p.scoredCriteriaCount,
                  total: p.totalCriteriaCount,
                })}
              </div>
            </TableCell>
            <TableCell>
              {p.isSubmitted ? (
                <Badge variant="default" className="bg-green-600">
                  {t('participantCoverage.submitted')}
                </Badge>
              ) : (
                <Badge variant="secondary">{t('participantCoverage.inProgress')}</Badge>
              )}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

/**
 * Attention Items Card Component
 */
function AttentionItemsCard({ items }: { items: AttentionItem[] }) {
  const { t } = useTranslation('results');

  const errors = items.filter(i => i.severity === 'error');
  const warnings = items.filter(i => i.severity === 'warning');

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('attentionItems.title')}</CardTitle>
        <CardDescription>{t('attentionItems.description')}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {errors.length > 0 && (
          <div>
            <h4 className="font-semibold mb-2 flex items-center gap-2">
              <AlertCircle className="h-4 w-4" />
              {t('attentionItems.errors', { count: errors.length })}
            </h4>
            <div className="space-y-2">
              {errors.map((item, i) => (
                <Alert key={i} variant="destructive">
                  <AlertTitle>{item.message}</AlertTitle>
                  <AlertDescription>
                    <ul className="list-disc list-inside mt-2">
                      {item.affectedItems.map((ai, j) => (
                        <li key={j}>
                          {ai.criterionTitle && <strong>{ai.criterionTitle}</strong>}
                          {ai.solutionName && ` (${ai.solutionName})`}
                          {ai.averageValue !== undefined &&
                            ` - ${t('attentionItems.averageScore', {
                              score: (ai.averageValue * 100).toFixed(1),
                              raw: ai.averageValue.toFixed(2),
                            })}`}
                        </li>
                      ))}
                    </ul>
                  </AlertDescription>
                </Alert>
              ))}
            </div>
          </div>
        )}

        {warnings.length > 0 && (
          <div>
            <h4 className="font-semibold mb-2 flex items-center gap-2">
              <AlertTriangle className="h-4 w-4" />
              {t('attentionItems.warnings', { count: warnings.length })}
            </h4>
            <div className="space-y-2">
              {warnings.map((item, i) => (
                <Alert key={i}>
                  <AlertTitle>{item.message}</AlertTitle>
                  <AlertDescription>
                    <ul className="list-disc list-inside mt-2">
                      {item.affectedItems.map((ai, j) => (
                        <li key={j}>
                          {ai.criterionTitle && <strong>{ai.criterionTitle}</strong>}
                          {ai.assignedCount !== undefined &&
                            ` - ${t('attentionItems.assignedCount', {
                              count: ai.assignedCount,
                            })}`}
                        </li>
                      ))}
                    </ul>
                  </AlertDescription>
                </Alert>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Scoring Ledger Card Component
 */
function ScoringLedgerCard({ summary }: { summary: EvaluationSummary }) {
  const { t } = useTranslation('results');

  const getImportanceBadgeVariant = (importance: Importance) => {
    switch (importance) {
      case Importance.Critical:
        return 'destructive';
      case Importance.High:
        return 'default';
      case Importance.Medium:
        return 'secondary';
      case Importance.Low:
        return 'outline';
      default:
        return 'outline';
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('scoringLedger.title')}</CardTitle>
        <CardDescription>{t('scoringLedger.description')}</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-1/3">{t('scoringLedger.criterion')}</TableHead>
                {summary.solutionScores.map(solution => (
                  <TableHead key={solution.solutionId} className="text-right">
                    {solution.solutionName}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {summary.ledger.groups.map(group => (
                <React.Fragment key={group.groupId}>
                  {/* Group Header */}
                  <TableRow className="bg-muted/50">
                    <TableCell
                      colSpan={summary.solutionScores.length + 1}
                      className="font-semibold"
                    >
                      {group.groupName} (
                      {t('scoringLedger.groupWeight', {
                        weight: (group.groupWeight * 100).toFixed(0),
                      })}
                      )
                    </TableCell>
                  </TableRow>

                  {/* Criteria Rows */}
                  {group.criteria.map(criterion => (
                    <TableRow key={criterion.criterionId}>
                      <TableCell className="pl-6">
                        <div className="flex items-center gap-2">
                          <span>{criterion.title}</span>
                          <Badge variant={getImportanceBadgeVariant(criterion.importance)}>
                            {criterion.importance}
                          </Badge>
                        </div>
                      </TableCell>
                      {criterion.solutionValues.map(sv => (
                        <TableCell key={sv.solutionId} className="text-right">
                          {sv.averageValue !== null ? (
                            <div>
                              <span className="font-medium">
                                {sv.averageValue.toFixed(3)}
                              </span>
                              <span className="text-xs text-muted-foreground ml-1">
                                ({sv.participantCount})
                              </span>
                            </div>
                          ) : (
                            <span className="text-muted-foreground">
                              {t('scoringLedger.noScore')}
                            </span>
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}

                  {/* Group Subtotal */}
                  <TableRow className="border-t-2 font-semibold">
                    <TableCell className="pl-6">{t('scoringLedger.groupSubtotal')}</TableCell>
                    {group.solutionSubtotals.map(subtotal => (
                      <TableCell key={subtotal.solutionId} className="text-right">
                        <div className="text-sm">
                          <div>{subtotal.rawSubtotal.toFixed(3)} avg</div>
                          <div className="text-primary">
                            {(subtotal.weightedSubtotal * 100).toFixed(1)} weighted
                          </div>
                        </div>
                      </TableCell>
                    ))}
                  </TableRow>
                </React.Fragment>
              ))}

              {/* Grand Total */}
              <TableRow className="border-t-4 bg-muted">
                <TableCell className="font-bold text-lg">
                  {t('scoringLedger.totalScore')}
                </TableCell>
                {summary.solutionScores.map(solution => (
                  <TableCell key={solution.solutionId} className="text-right">
                    <div className="text-xl font-bold text-primary">
                      {(solution.totalScore * 100).toFixed(1)}%
                    </div>
                  </TableCell>
                ))}
              </TableRow>
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}
